// The submission drawer: work the document checklist, set the target
// markets, and move the submission to the market.
//
// This was the last read-only drawer in the module — a screen you could
// create a record on and then do nothing with.

import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  C, Panel, Row, NextAction,
  money0, date, humanise,
} from './kit';
import { Ring } from './charts';
import {
  useAction, Notice, ActionButton, ActionRow, ConfirmAction, ToggleChecklist,
  Input, FieldRow,
  EditPanel, StatusAction, RemoveAction,
} from './actions';
import { ActivityTab } from './tabs';

export default function SubmissionDrawer({ row: r, tab, reload }) {
  const history = useHistory();
  const act = useAction(reload);
  const [docs, setDocs] = useState(r.requiredDocuments || []);
  const [market, setMarket] = useState('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDocs(r.requiredDocuments || []); setMarket(''); }, [r._id]);

  const done = docs.filter(d => d.uploaded).length;
  const ready = r.submissionStatus === 'ready_for_placement';

  // Ticking a document saves immediately — the progress ring is the point of
  // the panel, and a Save button in between makes it lie until pressed.
  const toggle = (i, next) => {
    const updated = docs.map((d, idx) => (idx === i
      ? { ...d, uploaded: next, uploadedAt: next ? new Date().toISOString() : undefined }
      : d));
    setDocs(updated);
    return act.run({
      method: 'patch',
      url: `/reinsurance/submissions/${r._id}`,
      body: { requiredDocuments: updated },
      success: null,
    });
  };

  const addMarket = () => {
    const name = market.trim();
    if (!name) return null;
    const next = [...(r.targetMarkets || []), name];
    setMarket('');
    return act.run({
      method: 'patch',
      url: `/reinsurance/submissions/${r._id}`,
      body: { targetMarkets: next },
      success: `${name} added to the target list.`,
    });
  };

  // ─── Documents ───
  if (tab === 'Documents') {
    return (
      <Panel title='Required documents'>
        <Notice error={act.error} note={act.note} />
        <ToggleChecklist
          items={docs}
          onToggle={toggle}
          saving={act.saving}
          countLabel={`${done} of ${docs.length} received`}
        />
      </Panel>
    );
  }

  // ─── Markets ───
  if (tab === 'Markets') {
    return (
      <>
        <Notice error={act.error} note={act.note} />
        <Panel title='Target markets'>
          {(r.targetMarkets || []).length === 0 ? (
            <div style={{ fontSize: 12.5, color: C.faint, lineHeight: 1.55 }}>
              No markets set. These are the ones you INTEND to approach — the gap
              between this list and who has actually been approached is the work
              still to do.
            </div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 }}>
              {(r.targetMarkets || []).map((m, i) => (
                <li key={m + i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 10, padding: '7px 2px',
                  borderBottom: i < r.targetMarkets.length - 1 ? `1px solid ${C.hair}` : 'none',
                  fontSize: 12.5, color: C.ink,
                }}>
                  <span>{m}</span>
                  <button
                    type='button'
                    onClick={() => act.run({
                      method: 'patch',
                      url: `/reinsurance/submissions/${r._id}`,
                      body: { targetMarkets: r.targetMarkets.filter((_, idx) => idx !== i) },
                      success: `${m} removed.`,
                    })}
                    style={{
                      border: 'none', background: 'none', cursor: 'pointer',
                      color: C.faint, fontSize: 11.5, fontFamily: 'inherit',
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title='Add a market'>
          <div style={{ display: 'grid', gap: 9 }}>
            <FieldRow>
              <Input
                label='Market'
                value={market}
                onChange={setMarket}
                placeholder='Swiss Re'
              />
            </FieldRow>
            <ActionRow>
              <ActionButton busy={act.saving} disabled={!market.trim()} onClick={addMarket}>
                Add to targets
              </ActionButton>
            </ActionRow>
          </div>
        </Panel>
      </>
    );
  }

  // ─── Activity ───
  if (tab === 'Activity') return <ActivityTab resource='submissions' id={r._id} />;


  // ─── Overview ───
  return (
    <>
      <Notice error={act.error} note={act.note} />

      <Panel title='Key information'>
        <Row label='Cedant' value={r.cedantName} />
        <Row label='Original insured' value={r.insuredName} />
        <Row label='Geography' value={r.geography || r.territory} />
        <Row label='Renewal date' value={date(r.periodStart)} />
        <Row label='Lead broker' value={r.leadBrokerName} />
        <Row label='Target markets' value={(r.targetMarkets || []).join(', ')} />
        <Row label='Currency' value={r.currency} />
        <Row label='Created' value={date(r.createdAt)} />
        <Row label='Last updated' value={date(r.updatedAt)} />
      </Panel>

      <Panel title='Coverage summary'>
        <Row label='Line of business' value={r.lineOfBusiness} />
        <Row label='Total insured value' value={money0(r.sumInsured, r.currency)} />
        <Row label='Expected limit' value={r.expectedLimit ? money0(r.expectedLimit, r.currency) : null} />
        <Row label='Layer' value={r.layerLabel || r.structureType} />
        <Row label='Territory' value={r.territory} />
      </Panel>

      {/* A COUNT, not a link. This was a ViewAllLink with no onClick, so it
          rendered as a blue arrow button that did nothing — and it says the
          same thing as the Ring immediately below it. A label cannot be
          clicked and so cannot disappoint. */}
      <Panel
        title='Required documents'
        action={<span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>{`${done} of ${docs.length}`}</span>}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 12 }}>
          <Ring done={done} total={docs.length} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>
              {done} of {docs.length}
            </div>
            <div style={{ fontSize: 12, color: C.muted }}>documents received</div>
          </div>
        </div>
        <ToggleChecklist
          items={docs}
          onToggle={toggle}
          saving={act.saving}
          countLabel={`${done} of ${docs.length} received`}
        />
      </Panel>

      <Panel title='Progress'>
        <FieldRow>
          <Input
            label='Submission status'
            value={r.submissionStatus || 'draft'}
            onChange={(v) => act.run({
              method: 'patch',
              url: `/reinsurance/submissions/${r._id}`,
              body: { submissionStatus: v },
              success: `Moved to ${humanise(v)}.`,
            })}
            options={[
              { value: 'draft', label: 'Draft' },
              { value: 'reviewing', label: 'Reviewing' },
              { value: 'awaiting_info', label: 'Awaiting Info' },
              { value: 'urgent', label: 'Urgent' },
              { value: 'ready_for_placement', label: 'Ready for Placement' },
              { value: 'withdrawn', label: 'Withdrawn' },
            ]}
          />
        </FieldRow>
      </Panel>

      {/* ── The record itself ──
          Correcting, moving and withdrawing a record, in one place on the
          Overview tab. Before this a screen could create a record and then
          never touch it again. Removal is a void or a cancel, never a
          delete — see reinsuranceShared.js. */}
      <Panel title='Record'>
        <div style={{ display: 'grid', gap: 10 }}>
          <EditPanel
            record={r}
            endpoint='/reinsurance/submissions'
            onSaved={reload}
            fields={[
              { key: 'cedantName', label: 'Cedant' },
              { key: 'insuredName', label: 'Insured' },
              { key: 'lineOfBusiness', label: 'Line of business' },
              { key: 'territory', label: 'Territory' },
              { key: 'handlerName', label: 'Handler' },
            ]}
          />
          {/* A submission's state is `submissionStatus`, not `status` —
              sending the wrong key writes nothing and still returns 200. */}
          <StatusAction
            record={r}
            endpoint='/reinsurance/submissions'
            field='submissionStatus'
            onSaved={reload}
            options={[{ value: 'draft', label: 'Draft' }, { value: 'reviewing', label: 'Reviewing' }, { value: 'awaiting_info', label: 'Awaiting Info' }, { value: 'ready_for_placement', label: 'Ready for Placement' }, { value: 'urgent', label: 'Urgent' }, { value: 'withdrawn', label: 'Withdrawn' }]}
          />
          <RemoveAction record={r} endpoint='/reinsurance/submissions' noun='submission' onRemoved={reload} />
        </div>
      </Panel>

      <Panel title='Actions'>
        <ActionRow>
          {!ready && (
            <ConfirmAction
              label='Send to market'
              question={`Mark ${r.reference} ready for placement with ${done} of ${docs.length} documents in? Markets will be asked to quote on what is here.`}
              confirmLabel='Send to market'
              busy={act.saving}
              onConfirm={() => act.run({
                method: 'patch',
                url: `/reinsurance/submissions/${r._id}`,
                body: { submissionStatus: 'ready_for_placement' },
                success: 'Ready for placement — open it under Placement to approach markets.',
              })}
            />
          )}
          <ActionButton tone='quiet' onClick={() => history.push('/reinsurance/placement')}>
            Open placement
          </ActionButton>
        </ActionRow>
      </Panel>

      {r.nextAction?.label && (
        <NextAction label={r.nextAction.label} detail={r.nextAction.detail} />
      )}
    </>
  );
}
